/**
 * Renders rows as text: SQL INSERT statements, CSV, or JSON. Pure functions over
 * plain rows and column names, so any grid can offer them
 * (FEATURE-20261013-sql-query-edit-data-and-selection-actions-over-the-mesh).
 */

export type DumpRow = Record<string, unknown>;

/** A SQL identifier, quoted only when it is not a plain lower-case name. */
export function sqlIdent(name: string): string {
  return /^[a-z_][a-z0-9_]*$/.test(name) ? name : `"${name.replace(/"/g, '""')}"`;
}

/** A SQL literal for a cell value: NULL, a bare number or boolean, else a quoted string. */
export function sqlLiteral(value: unknown): string {
  if (value === null || value === undefined) return "NULL";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "NULL";
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return `'${text.replace(/'/g, "''")}'`;
}

/** One INSERT per row. `table` may be dotted (schema.table); each part is quoted as needed. */
export function dumpAsSql(rows: DumpRow[], columns: string[], table: string): string {
  const target = table.split(".").map(sqlIdent).join(".");
  const cols = columns.map(sqlIdent).join(", ");
  return rows
    .map((row) => `INSERT INTO ${target} (${cols}) VALUES (${columns.map((c) => sqlLiteral(row[c])).join(", ")});`)
    .join("\n");
}

function csvField(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : typeof value === "object" ? JSON.stringify(value) : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** RFC 4180: a header line, CRLF line ends, a field quoted when it holds a comma, quote or newline. */
export function dumpAsCsv(rows: DumpRow[], columns: string[]): string {
  const lines = [columns.map(csvField).join(",")];
  for (const row of rows) lines.push(columns.map((c) => csvField(row[c])).join(","));
  return lines.join("\r\n");
}

/** An array of objects holding only the named columns, in column order. */
export function dumpAsJson(rows: DumpRow[], columns: string[]): string {
  return JSON.stringify(
    rows.map((row) => Object.fromEntries(columns.map((c) => [c, row[c] ?? null]))),
    null,
    2,
  );
}
